/*
 * Copyright 2026 Sony Corporation
 */

import type { Device } from '@ips-device-verse/twin-service/schema';

import { listPriceOf } from './camera-models';
import type { Transform } from '../scene/types';

const DEGREES = 180 / Math.PI;

export interface BomLine {
  model: string;
  quantity: number;
  /** Absent when the model is not in the price table. */
  unitPrice: number | undefined;
  subtotal: number | undefined;
}

export interface BomTotal {
  lines: BomLine[];
  devices: number;
  /** Sum of the priced lines only. */
  priced: number;
  /** True when at least one model has no price, so `priced` is not the whole bill. */
  incomplete: boolean;
}

/** One line per model, which is the shape a purchase order wants. */
export const rollUp = (devices: Device[]): BomTotal => {
  const counts = new Map<string, number>();
  for (const device of devices) {
    counts.set(device.model, (counts.get(device.model) ?? 0) + 1);
  }

  const lines = [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([model, quantity]) => {
      const unitPrice = listPriceOf(model);
      return { model, quantity, unitPrice, subtotal: unitPrice === undefined ? undefined : unitPrice * quantity };
    });

  return {
    lines,
    devices: devices.length,
    priced: lines.reduce((sum, line) => sum + (line.subtotal ?? 0), 0),
    incomplete: lines.some((line) => line.unitPrice === undefined),
  };
};

/**
 * A spreadsheet treats a cell opening with one of these as a formula, so a
 * device named `=cmd|…` would execute on open. Prefixing an apostrophe keeps
 * it text.
 */
const defuse = (value: string): string => (/^[=+\-@\t\r]/.test(value) ? `'${value}` : value);

const cell = (value: string | number | undefined): string => {
  if (value === undefined) {
    return '';
  }
  const text = typeof value === 'number' ? String(value) : defuse(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const row = (...values: (string | number | undefined)[]): string => values.map(cell).join(',');

/**
 * The roll-up a buyer signs off, then the per-device detail an installer needs,
 * as two labelled blocks in one file.
 */
export const bomCsv = (worldName: string, devices: Device[], poses: Record<string, Transform>): string => {
  const { lines, priced, incomplete } = rollUp(devices);

  const out = [
    row('World', worldName),
    row('Generated', new Date().toISOString()),
    '',
    row('Model', 'Quantity', 'Unit price (USD)', 'Subtotal (USD)'),
    ...lines.map((line) => row(line.model, line.quantity, line.unitPrice, line.subtotal)),
    row('Total', devices.length, '', priced),
  ];

  if (incomplete) {
    out.push(row('Note', 'Some models have no list price; the total covers the priced lines only.'));
  }

  out.push(
    '',
    row('Device', 'Name', 'Model', 'Address', 'X (m)', 'Y (m)', 'Z (m)', 'Rot X (deg)', 'Rot Y (deg)', 'Rot Z (deg)')
  );

  for (const device of devices) {
    const pose = poses[device.device_id];
    out.push(
      row(
        device.device_id,
        device.name,
        device.model,
        device.address,
        pose && pose.position.x.toFixed(2),
        pose && pose.position.y.toFixed(2),
        pose && pose.position.z.toFixed(2),
        pose && (pose.rotation.x * DEGREES).toFixed(1),
        pose && (pose.rotation.y * DEGREES).toFixed(1),
        pose && (pose.rotation.z * DEGREES).toFixed(1)
      )
    );
  }

  return out.join('\n');
};
